import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from 'reactstrap';
import Swal from 'sweetalert2';

const ConsentFormModal = ({ 
  isOpen, 
  toggle, 
  bookingId, 
  customerName, 
  customerMobile,
  isConsentSigned,
  witnessName: initialWitnessName,
  witnessSignature: initialWitnessSignature,
  guestSignature: initialGuestSignature,
  consentSignedDate,
  onSignConsent,
  loading 
}) => {
  const [witnessName, setWitnessName] = useState(initialWitnessName || '');
  const [guestSignature, setGuestSignature] = useState(initialGuestSignature || '');
  const [witnessSignature, setWitnessSignature] = useState(initialWitnessSignature || '');
  
  const guestCanvasRef = useRef(null);
  const witnessCanvasRef = useRef(null);
  const guestIsDrawingRef = useRef(false);
  const witnessIsDrawingRef = useRef(false);

  useEffect(() => {
    setWitnessName(initialWitnessName || '');
    setGuestSignature(initialGuestSignature || '');
    setWitnessSignature(initialWitnessSignature || '');
    
    // Initialize canvas and load signatures when modal opens
    if (isOpen) {
      // Use setTimeout to ensure canvas is rendered before we try to access it
      setTimeout(() => {
        const guestCanvas = guestCanvasRef.current;
        const witnessCanvas = witnessCanvasRef.current;
        
        // Load signatures if they exist (either from DB or from state before booking save)
        if (initialGuestSignature && guestCanvas) {
          loadSignatureToCanvas(guestCanvas, initialGuestSignature);
        } else if (guestCanvas) {
          initializeCanvas(guestCanvas);
        }
        
        if (initialWitnessSignature && witnessCanvas) {
          loadSignatureToCanvas(witnessCanvas, initialWitnessSignature);
        } else if (witnessCanvas) {
          initializeCanvas(witnessCanvas);
        }
      }, 100);
    }
  }, [isOpen, initialWitnessName, initialGuestSignature, initialWitnessSignature, isConsentSigned]);

  const initializeCanvas = (canvas) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  const loadSignatureToCanvas = (canvas, base64String) => {
    if (!canvas || !base64String) {
      console.log('loadSignatureToCanvas: Missing canvas or base64String', { canvas: !!canvas, base64String: base64String?.substring(0, 50) });
      return;
    }
    
    // Check if base64 string is valid (not truncated)
    // A valid PNG base64 should be at least 100 characters
    if (base64String.length < 100) {
      console.warn('loadSignatureToCanvas: Base64 string seems too short, might be truncated', {
        length: base64String.length,
        preview: base64String.substring(0, 100)
      });
      // Don't return, try to load anyway in case it's a very small signature
    }
    
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      try {
        // Clear canvas first
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Fill with white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Calculate scaling to fit the image in canvas while maintaining aspect ratio
        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
        const x = (canvas.width - img.width * scale) / 2;
        const y = (canvas.height - img.height * scale) / 2;
        
        // Draw the signature image scaled to fit
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
        console.log('Signature loaded successfully to canvas', {
          imgSize: { width: img.width, height: img.height },
          canvasSize: { width: canvas.width, height: canvas.height },
          scale: scale
        });
      } catch (error) {
        console.error('Error drawing signature to canvas:', error);
      }
    };
    
    img.onerror = (error) => {
      console.error('Error loading signature image:', error, {
        base64Length: base64String.length,
        base64Preview: base64String.substring(0, 100),
        hasDataPrefix: base64String.startsWith('data:')
      });
      // Initialize empty canvas on error
      initializeCanvas(canvas);
    };
    
    // Handle both data URL and plain base64
    // Remove any whitespace that might have been added
    const cleanBase64 = base64String.trim();
    const imageSrc = cleanBase64.startsWith('data:') ? cleanBase64 : `data:image/png;base64,${cleanBase64}`;
    
    console.log('Loading signature image', {
      base64Length: base64String.length,
      imageSrcLength: imageSrc.length,
      startsWithData: imageSrc.startsWith('data:')
    });
    
    img.src = imageSrc;
  };

  const startDrawing = (e, isGuest) => {
    if (isConsentSigned) return; // Don't allow drawing if already signed
    const canvas = isGuest ? guestCanvasRef.current : witnessCanvasRef.current;
    const isDrawingRef = isGuest ? guestIsDrawingRef : witnessIsDrawingRef;
    
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    isDrawingRef.current = true;
    ctx.beginPath();
    ctx.moveTo(
      e.clientX - rect.left,
      e.clientY - rect.top
    );
  };

  const draw = (e, isGuest) => {
    if (isConsentSigned) return;
    const canvas = isGuest ? guestCanvasRef.current : witnessCanvasRef.current;
    const isDrawingRef = isGuest ? guestIsDrawingRef : witnessIsDrawingRef;
    
    if (!isDrawingRef.current || !canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    ctx.lineTo(
      e.clientX - rect.left,
      e.clientY - rect.top
    );
    ctx.stroke();
  };

  const stopDrawing = (isGuest) => {
    if (isConsentSigned) return;
    const isDrawingRef = isGuest ? guestIsDrawingRef : witnessIsDrawingRef;
    isDrawingRef.current = false;
  };

  const clearSignature = (isGuest) => {
    if (isConsentSigned) return;
    const canvas = isGuest ? guestCanvasRef.current : witnessCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    if (isGuest) {
      setGuestSignature('');
    } else {
      setWitnessSignature('');
    }
  };

  const getSignatureBase64 = (canvas) => {
    if (!canvas) return '';
    return canvas.toDataURL('image/png');
  };

  const handleSign = async () => {
    if (isConsentSigned) {
      toggle();
      return;
    }

    // Get signatures from canvas
    const guestSig = getSignatureBase64(guestCanvasRef.current);
    const witnessSig = getSignatureBase64(witnessCanvasRef.current);

    // Both signatures are optional, but at least one should be provided
    if (!guestSig || guestSig === guestCanvasRef.current?.toDataURL('image/png').split(',')[0] + ',') {
      if (!witnessSig || witnessSig === witnessCanvasRef.current?.toDataURL('image/png').split(',')[0] + ',') {
        Swal.fire("Warning", "Please provide at least one signature (Guest or Witness)", "warning");
        return;
      }
    }

    try {
      const consentData = {
        booking_Id: bookingId || 0, // Allow 0 for pre-save capture
        witnessName: witnessName.trim() || null,
        witnessSignature: witnessSig || null,
        guestSignature: guestSig || null,
      };

      await onSignConsent(consentData);
      toggle();
    } catch (error) {
      console.error('Error signing consent:', error);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        {isConsentSigned ? 'General Consent Form (Signed)' : 'General Consent Form'}
      </ModalHeader>
      <ModalBody style={{ maxHeight: '80vh', overflowY: 'auto' }}>
        <div style={{ 
          backgroundColor: '#2d2d2d', 
          color: '#ffffff', 
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <h4 style={{ textAlign: 'center', marginBottom: '20px', fontWeight: 'bold' }}>
            GENERAL CONSENT FORM
          </h4>
          
          <div style={{ marginBottom: '15px', fontWeight: 'bold', textAlign: 'center' }}>
            DO NOT SIGN THIS FORM WITHOUT READING AND UNDERSTANDING ITS CONTENTS
          </div>

          <Row className="mb-3">
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: '#ffffff' }}>Guest Name:</Label>
                <Input 
                  type="text" 
                  value={customerName || ''} 
                  readOnly
                  style={{ backgroundColor: '#3d3d3d', color: '#ffffff', border: '1px solid #555' }}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: '#ffffff' }}>Mobile No:</Label>
                <Input 
                  type="text" 
                  value={customerMobile || ''} 
                  readOnly
                  style={{ backgroundColor: '#3d3d3d', color: '#ffffff', border: '1px solid #555' }}
                />
              </FormGroup>
            </Col>
          </Row>

          <div style={{ fontSize: '12px', lineHeight: '1.6', marginBottom: '20px' }}>
            <p><strong>1.</strong> I acknowledge that I have read and understood the procedures, benefits, and inherent risks associated with the spa services I will receive.</p>
            <p><strong>2.</strong> I understand that spa service fees are paid upon check-in, services are final and non-refundable, and unused appointments can be rescheduled.</p>
            <p><strong>3.</strong> I will provide valid ID, declare allergies and medical conditions, and understand that pregnant guests should consult doctors. Guests on their menstrual period are not allowed in the wet area.</p>
            <p><strong>4.</strong> I understand that guests under 16 are not allowed, guests under 18 require supervision, alcohol and smoking are prohibited, pets are not allowed, and guests with claustrophobia should avoid Korean Spa Capsules.</p>
            <p><strong>5.</strong> I will adhere to recommended times for package facilities.</p>
            <p><strong>6.</strong> I understand locker facility usage, electronic keys, mobile phone use with waterproof pouches, and the process for returning keys and IDs upon check-out, including penalties for missing items.</p>
            <p><strong>7.</strong> I will follow package sequence, exercise caution on wet surfaces, remove shoes before entering the spa area, shower in designated areas, wear provided spa slippers (except in wet area), and disposable undergarments.</p>
            <p><strong>8.</strong> I will not manipulate facility settings (Sauna, Steam, Jacuzzi, Korean Shower), touch hot/metallic surfaces, and will remove contact lenses during hot water services. I can contact a technician for assistance.</p>
            <p><strong>9.</strong> I will leave steam/sauna if feeling dizzy/uncomfortable and ring a bell for staff. I understand the importance of hydration before using hot facilities and that packaged drinking water is provided.</p>
            <p><strong>10.</strong> I understand that sauna, steam, and infrared sauna are limited to 2 persons at a time and will ring an emergency bell or notify staff in emergencies.</p>
            <p><strong>11.</strong> I will wear a spa uniform, including a Korean lamb head towel, before entering the dry area.</p>
            <p><strong>12.</strong> I understand that I may receive complimentary items depending on my availed packages/services.</p>
          </div>

          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#3d3d3d', borderRadius: '5px' }}>
            <p style={{ marginBottom: '10px' }}>
              <strong>I hereby declare that I have read and understood all terms and conditions indicated in this document and I confirm that I do my consent in every point made.</strong>
            </p>
          </div>

          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#3d3d3d', borderRadius: '5px' }}>
            <p style={{ fontSize: '12px', marginBottom: '10px' }}>
              <strong>Consent for Marketing Communication:</strong> I hereby give my consent to receive marketing communication from Annyeong Spa and Beauty Center LLC through the email address and mobile number provided. This includes promotional offers, appointment reminders, service updates, and loyalty program notifications. I understand that I may withdraw my consent at any time by notifying the spa or opting out through the provided communication channels.
            </p>
          </div>

          {isConsentSigned && consentSignedDate && (
            <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#1a5a1a', borderRadius: '5px', textAlign: 'center' }}>
              <strong>Signed on: {new Date(consentSignedDate).toLocaleString()}</strong>
            </div>
          )}

          {/* Warning if signatures appear truncated */}
          {isConsentSigned && (
            (initialGuestSignature && initialGuestSignature.length < 200) || 
            (initialWitnessSignature && initialWitnessSignature.length < 200)
          ) && (
            <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#8b4513', borderRadius: '5px', textAlign: 'center' }}>
              <strong style={{ color: '#ffcccc' }}>
                ⚠️ Warning: Signature data appears to be truncated. Please check backend configuration for NVARCHAR(MAX) fields.
              </strong>
            </div>
          )}

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: '#ffffff' }}>Guest Signature {!isConsentSigned && <span className="text-danger">*</span>}</Label>
                <div style={{ border: '2px solid #555', borderRadius: '5px', padding: '10px', backgroundColor: '#ffffff' }}>
                  <canvas
                    ref={guestCanvasRef}
                    width={300}
                    height={150}
                    style={{ 
                      border: '1px solid #ccc', 
                      cursor: isConsentSigned ? 'default' : 'crosshair',
                      display: 'block',
                      width: '100%',
                      maxWidth: '300px'
                    }}
                    onMouseDown={(e) => startDrawing(e, true)}
                    onMouseMove={(e) => draw(e, true)}
                    onMouseUp={() => stopDrawing(true)}
                    onMouseLeave={() => stopDrawing(true)}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      const touch = e.touches[0];
                      const mouseEvent = new MouseEvent('mousedown', {
                        clientX: touch.clientX,
                        clientY: touch.clientY
                      });
                      startDrawing(mouseEvent, true);
                    }}
                    onTouchMove={(e) => {
                      e.preventDefault();
                      const touch = e.touches[0];
                      const mouseEvent = new MouseEvent('mousemove', {
                        clientX: touch.clientX,
                        clientY: touch.clientY
                      });
                      draw(mouseEvent, true);
                    }}
                    onTouchEnd={() => stopDrawing(true)}
                  />
                </div>
                {!isConsentSigned && (
                  <Button 
                    color="secondary" 
                    size="sm" 
                    onClick={() => clearSignature(true)}
                    style={{ marginTop: '5px' }}
                  >
                    Clear
                  </Button>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: '#ffffff' }}>Witness Signature</Label>
                <div style={{ border: '2px solid #555', borderRadius: '5px', padding: '10px', backgroundColor: '#ffffff' }}>
                  <canvas
                    ref={witnessCanvasRef}
                    width={300}
                    height={150}
                    style={{ 
                      border: '1px solid #ccc', 
                      cursor: isConsentSigned ? 'default' : 'crosshair',
                      display: 'block',
                      width: '100%',
                      maxWidth: '300px'
                    }}
                    onMouseDown={(e) => startDrawing(e, false)}
                    onMouseMove={(e) => draw(e, false)}
                    onMouseUp={() => stopDrawing(false)}
                    onMouseLeave={() => stopDrawing(false)}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      const touch = e.touches[0];
                      const mouseEvent = new MouseEvent('mousedown', {
                        clientX: touch.clientX,
                        clientY: touch.clientY
                      });
                      startDrawing(mouseEvent, false);
                    }}
                    onTouchMove={(e) => {
                      e.preventDefault();
                      const touch = e.touches[0];
                      const mouseEvent = new MouseEvent('mousemove', {
                        clientX: touch.clientX,
                        clientY: touch.clientY
                      });
                      draw(mouseEvent, false);
                    }}
                    onTouchEnd={() => stopDrawing(false)}
                  />
                </div>
                {!isConsentSigned && (
                  <Button 
                    color="secondary" 
                    size="sm" 
                    onClick={() => clearSignature(false)}
                    style={{ marginTop: '5px' }}
                  >
                    Clear
                  </Button>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: '#ffffff' }}>Date of Consent:</Label>
                <Input 
                  type="text" 
                  value={(() => {
                    const date = consentSignedDate ? new Date(consentSignedDate) : new Date();
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    return `${day}/${month}/${year}`;
                  })()} 
                  readOnly
                  style={{ backgroundColor: '#3d3d3d', color: '#ffffff', border: '1px solid #555' }}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label style={{ color: '#ffffff' }}>Witness Name:</Label>
                <Input 
                  type="text" 
                  value={witnessName}
                  onChange={(e) => setWitnessName(e.target.value)}
                  disabled={isConsentSigned}
                  placeholder="Enter witness name"
                  style={{ backgroundColor: isConsentSigned ? '#3d3d3d' : '#ffffff', color: isConsentSigned ? '#ffffff' : '#000000', border: '1px solid #555' }}
                />
              </FormGroup>
            </Col>
          </Row>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          {isConsentSigned ? 'Close' : 'Cancel'}
        </Button>
        {!isConsentSigned && (
          <Button color="primary" onClick={handleSign} disabled={loading}>
            {loading ? 'Signing...' : 'Sign Consent'}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ConsentFormModal;

